import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { AnnualReportProvider } from '../../../../../../aumico-lib-annualreport-fe/src/lib';
import { SidenavService } from '../../../../../../aumico-lib-core-fe/src/lib';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { LastVisitedImportTabService } from '../services/last-visited-import-tab.service';

const debug = Debug('aumico:saas:annualReportFinancialStatementImport.page');
@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-import-page',
    templateUrl: 'annualReportFinancialStatementImport.page.html',
    styleUrls: ['annualReportFinancialStatementImport.page.scss'],
})
export class AnnualReportFinancialStatementImportPage implements OnInit {
    fileChanged: boolean;

    public activeNavigationTabs: {
        routerLink: string;
        tabLabelKey: string;
    }[] = [
            {
                routerLink: Paths.UPLOAD,
                tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_UPLOAD,
            },
            {
                routerLink: Paths.MAPPING,
                tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_MAPPING,
            },
            {
                routerLink: Paths.GROUPING_MASKING,
                tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_GROUPING_MASKING,
            },
        ];

    private readonly fxTab = {
        routerLink: Paths.FX,
        tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_FX,
    };

    constructor(
        private readonly sidenavService: SidenavService,
        private router: Router,
        private lastVisitedImportTabService: LastVisitedImportTabService,
        private annualReportProvider: AnnualReportProvider,
    ) {

    }

    onAuditFileChanged(event: boolean): void {
        this.fileChanged = event;
    }

    ngOnInit(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                switchMap((event: NavigationEnd) => {
                    const urlSegments = event.urlAfterRedirects.split('/');
                    const currentTab = urlSegments[urlSegments.length - 1];

                    // Only save valid tabs, not the base 'import' route
                    if (
                        currentTab === Paths.UPLOAD ||
                        currentTab === Paths.MAPPING ||
                        currentTab === Paths.GROUPING_MASKING ||
                        currentTab === Paths.FX
                    ) {
                        return this.annualReportProvider.getActiveAnnualReport$().pipe(
                            filter((ar) => ar != null && ar._id != null),
                            take(1),
                            switchMap((ar) => this.lastVisitedImportTabService.setLastVisitedTab(ar._id, currentTab)),
                        );
                    }
                    return of(null);
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.sidenavService.sideNavState$.next(false);

        // The FX tab only exists on a Functional Currency report. FxGuard covers the URL; this
        // covers the link.
        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(untilDestroyed(this))
            .subscribe((annualReport) => {
                const isFxReport = annualReport?.setupData?.fxEnabled === true;
                const isTabPresent = this.activeNavigationTabs.some((tab) => tab.routerLink === Paths.FX);

                if (isFxReport && !isTabPresent) {
                    this.activeNavigationTabs = [...this.activeNavigationTabs, this.fxTab];
                } else if (!isFxReport && isTabPresent) {
                    this.activeNavigationTabs = this.activeNavigationTabs.filter(
                        (tab) => tab.routerLink !== Paths.FX,
                    );
                }
            });
    }
}
